import React, { useState, useEffect } from "react";
import { Avatar, CardMedia, Grid, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";

import CustomChip from "components/common/Chip";
import CustomTooltip from "components/common/Tooltip";
import {
  convertDate,
  getCountryFlag,
  getProfileImage,
  getSalaryWithoutBouns,
  getValue,
  getWorkingDuration,
} from "../Screens/CommonUtils";
import { s3ImagesBaseURL } from "util/APIBaseUrl";

import avatarImage from "assets/images/fallback-image.svg";
import CreditCardIcon from "assets/svgs/CreditCardIcon";
import CalendarIcon from "assets/svgs/CalendarIcon";
import RedDotIcon from "assets/svgs/RedDotIcon";
import YellowDotIcon from "assets/svgs/YellowDotIcon";

import { usePermissions } from "components/Hook";

import "./WorkforceCard.css";

function WorkforceCard({ data, isEmployee, isCLickable = false }) {
  const { temporaryReplacement } = useFlags();
  const navigate = useNavigate();
  const [imageSrc, setImageSrc] = useState(avatarImage);
  const { canAccessFinancials } = usePermissions();
  const showFinancials = canAccessFinancials();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const lastLeaveDate = new Date(data?.leaveStatus?.dateTo);
  lastLeaveDate.setHours(0, 0, 0, 0);

  const { substituteOf, joiningDate, endDate } = data?.employmentHistory || {};
  const { firstName, lastName } = substituteOf || {};

  const truncateString = (str, num) => {
    if (str.length <= num) {
      return str;
    }
    return `${str.slice(0, num)}...`;
  };

  const handleCardClick = () => {
    navigate(`/main/details/${data?.employeeId}`);
  };

  useEffect(() => {
    if (data?.picturePath) {
      getProfileImage(data?.employeeId, data?.pictureVersion).then((img) => {
        setImageSrc(img);
      });
    } else {
      setImageSrc(avatarImage);
    }
  }, [data?.employeeId, data?.pictureVersion]);

  return (
    <div className="workforce-card-container" onClick={isCLickable ? handleCardClick : undefined}>
      <div style={{ position: "relative" }}>
        <div className="workforce-card-image-wrapper">
          <CardMedia
            component="img"
            minHeight="100"
            image={
              imageSrc[data?.employeeId] ? imageSrc[data?.employeeId] : `${s3ImagesBaseURL}/placeholder-avatar.svg`
            }
            sx={{
              height: "136px",
              objectFit: "cover",
              objectPosition: "center",
              width: "100%",
            }}
          />
        </div>

        {getCountryFlag(data?.country) && (
          <Avatar
            sx={{
              width: "26px",
              height: "26px",
              background: "#FFFFFF",
              borderRadius: "99px",
              position: "absolute",
              bottom: "-14px",
              left: "15px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            id="country-flag"
            alt={data?.country}
          >
            {getCountryFlag(data?.country)}
          </Avatar>
        )}

        {temporaryReplacement && data?.employeeStatus === "Substitute" && (
          <CustomTooltip
            content={`Substituting ${getValue(firstName)} ${getValue(lastName)} from ${convertDate(joiningDate)} to ${convertDate(endDate)}`}
            direction="bottom"
          >
            <div
              style={{
                position: "absolute",
                bottom: "-14px",
                right: "15px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <CustomChip label="Substitute" size="small" color="warning" />
            </div>
          </CustomTooltip>
        )}
      </div>
      <div className="pt-20p pb-0p">
        <div className="flex flex-col">
          <div className="flex flex-col px-10p">
            <p className="heading-h5-semibold">{`${getValue(data?.firstName)} ${getValue(data?.lastName)}`}</p>
            <p className="para-body-s-regular text-headings-secondary">{getValue(data?.jobTitle)}</p>
          </div>
          {!isEmployee ? (
            <Grid>
              {showFinancials && (
                <Grid
                  sx={{
                    background: "#FBFAFF",
                    padding: "6px 10px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <CreditCardIcon style={{ width: "16px", height: "16px" }} />
                  <Typography
                    sx={{
                      fontFamily: "Poppins-Regular",
                      fontWeight: 400,
                      fontSize: "12px",
                      lineHeight: "12px",
                      color: "#14151F",
                    }}
                    id="salary-without-bonus"
                  >
                    $ {getSalaryWithoutBouns(data?.costToCompany, data?.incrementDetails?.bonus)}
                  </Typography>
                </Grid>
              )}
              <Grid
                sx={{
                  background: "var(--color-primitives-primary-50)",
                  padding: "6px 10px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <CalendarIcon style={{ width: "16px", height: "16px" }} />
                <Typography
                  sx={{
                    fontFamily: "Poppins-Regular",
                    fontWeight: 400,
                    fontSize: "12px",
                    lineHeight: "12px",
                    color: "#14151F",
                  }}
                  id="work-duration"
                >
                  {getWorkingDuration(data?.startDate)}
                </Typography>
              </Grid>
              <div style={{ height: "20px" }}>
                {data?.leaveStatus?.status && lastLeaveDate >= today && (
                  <Grid
                    sx={{
                      background: "#FFFBF5",
                      padding: "6px 10px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {data?.leaveStatus?.statusId === "Out till" || data?.leaveStatus?.statusId === "Out today" ? (
                      <RedDotIcon />
                    ) : (
                      data?.leaveStatus?.statusId === "Out from" && <YellowDotIcon />
                    )}

                    <Typography
                      sx={{
                        fontFamily: "Poppins-Regular",
                        fontWeight: 400,
                        fontSize: "12px",
                        lineHeight: "12px",
                        color: "#14151F",
                      }}
                      id="emp-status"
                    >
                      {truncateString(
                        data?.leaveStatus?.statusId === "Out today"
                          ? data?.leaveStatus?.statusId
                          : data?.leaveStatus?.status,
                        15
                      )}
                    </Typography>
                  </Grid>
                )}
              </div>
            </Grid>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default WorkforceCard;
